import NumberFlow from '@number-flow/react'
import { useTranslation } from 'react-i18next'
import LucideImage from '~icons/lucide/image'
import MdiCheckCircle from '~icons/mdi/check-circle'
import { CreditsIcon } from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import BenefitCardIcon from 'src/features/Kaisuuken/assets/benefit-card-icon-video.svg?react'
import { cn, cx } from 'src/utils/cx'
import starStickerUrl from '../resources/star.svg'
import { formatLocalizedNumber } from '../utils/formatLocalizedNumber'
import type { OneTimePackCardProps } from './OneTimePackCard'

export function OneTimePackCardV3({
  title,
  rows,
  multiplier,
  boostedCredits,
  priceLabel,
  locale,
  animateCredits,
  equivalentImagesText,
  featured,
  busy,
  disabled,
  showSuccess,
  hasKaisuuken,
  onPurchase,
}: OneTimePackCardProps) {
  const { t } = useTranslation(['membership'])
  const nonTotalRows = rows.filter(row => !row.total)
  const totalRow = rows.find(row => row.total)
  const hasMiddleSection = nonTotalRows.length > 0 || Boolean(totalRow)

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-2xl font-inter transition h-full',
        'w-full min-w-0 sm:min-w-48 md:min-w-56',
        // Outer card is always the standard card background — the lavender
        // featured tint lives on the top section only. `overflow` stays
        // visible so the sun-sticker badge can float past the corner.
        'bg-[var(--credit-card-background)] dark:bg-[var(--credit-card-background-dark)]',
        'ring-1',
        featured
          ? 'ring-dspurple-400/50'
          : 'ring-[var(--credit-card-border)] dark:ring-[var(--credit-card-border-dark)]',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-lg',
      )}
      onClick={disabled ? undefined : onPurchase}
    >
      {multiplier && <SunStickerBadge multiplier={multiplier} />}

      <div
        className={cn(
          'relative px-4 pt-6 pb-3 flex-1 flex flex-col items-center gap-1.5 rounded-t-2xl',
          featured &&
            'bg-gradient-to-b from-dspurple-400/15 to-transparent dark:from-dspurple-500/20 dark:to-transparent',
        )}
      >
        <div className="relative flex items-center justify-center gap-1.5 mb-1">
          <CreditsIcon className="text-dspurple-400 size-10 md:size-12" />
          {hasKaisuuken && (
            <BenefitCardIcon className="text-dspurple-400 size-8 md:size-10" />
          )}
        </div>

        {title && (
          <div className="relative text-xs md:text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {title}
          </div>
        )}

        <div className="relative flex items-center gap-1.5">
          <CreditsIcon className="text-dspurple-400 size-6 md:size-7 shrink-0" />
          <span className="text-2xl md:text-3xl font-bold leading-none text-neutral-900 dark:text-neutral-50">
            {animateCredits ? (
              <NumberFlow value={boostedCredits} />
            ) : (
              formatLocalizedNumber(boostedCredits, locale)
            )}
          </span>
        </div>

        {equivalentImagesText && (
          <div className="relative mt-0.5 inline-flex items-center gap-1 rounded-full bg-[var(--pixWarning,hsla(29,96%,59%,1))] dark:bg-[var(--pixWarningDark,hsla(25,95%,53%,1))] px-2.5 py-0.5 text-xs font-semibold text-[var(--pixWhite,hsla(270,100%,100%,1))]">
            <LucideImage className="size-3" aria-hidden="true" />
            <span>{equivalentImagesText}</span>
          </div>
        )}
      </div>

      {hasMiddleSection && (
        <div className="px-4 pt-3 pb-3 flex flex-col gap-1.5">
          {nonTotalRows.length > 0 && (
            <div className="w-full flex flex-wrap justify-center gap-1.5">
              {nonTotalRows.map(row => (
                <span
                  key={`${row.label}-${row.value}`}
                  className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:text-neutral-300"
                >
                  <span className="truncate">{row.label}</span>
                  <span className="font-semibold">{row.value}</span>
                </span>
              ))}
            </div>
          )}

          {totalRow && (
            <div className="w-full flex items-baseline justify-between text-xs md:text-sm">
              <span className="text-neutral-500 dark:text-neutral-400 truncate">
                {totalRow.label}
              </span>
              <span className="font-bold text-neutral-900 dark:text-neutral-100 shrink-0">
                {totalRow.value}
              </span>
            </div>
          )}
        </div>
      )}

      <UIButton
        className={cx(
          'h-10 md:h-12 flex-none shrink-0 items-center z-10 justify-center text-sm md:text-base font-bold transition-all duration-200 rounded-t-none rounded-b-2xl',
          disabled
            ? 'bg-black/15 text-black/50 hover:bg-black/15 dark:bg-white/15 dark:text-white/50 dark:hover:bg-white/15'
            : showSuccess
              ? 'bg-green-700'
              : 'bg-dspurple-500 hover:bg-dspurple-500/90 dark:bg-dspurple-500 dark:hover:bg-dspurple-500',
        )}
        labelClassName="dark:text-white"
        variant="filled"
        busy={busy ? 'auto' : false}
        disabled={disabled}
        onPress={onPurchase}
      >
        {showSuccess ? (
          <MdiCheckCircle className="size-5 inset-0 m-auto text-white animate-[scale-up_300ms_cubic-bezier(0.34,1.56,0.64,1)]" />
        ) : (
          priceLabel
        )}
      </UIButton>
    </div>
  )
}

/**
 * Orange 12-point star sticker (Figma export at ../resources/star.svg) with
 * stacked {multiplier}x / Value label. The SVG includes its own drop shadow
 * (filter id `filter0_d_3982_23690`), so no extra `drop-shadow-*` class here.
 *
 * The SVG viewBox is 84×83 but the star itself is bounded by ~80×75 in the
 * top-left — the rest is shadow padding. We nudge the text up-left slightly
 * so it lands on the visual centre of the star rather than the SVG centre.
 */
function SunStickerBadge({ multiplier }: { multiplier: string }) {
  const { t } = useTranslation(['membership'])
  return (
    <div className="absolute -top-5 -left-5 z-20 grid place-items-center size-16 md:size-20 pointer-events-none">
      <img
        src={starStickerUrl}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 size-full"
      />
      <div className="relative flex flex-col items-center justify-center text-white leading-none -translate-x-[2px] -translate-y-[2px]">
        <span className="text-sm md:text-base font-bold">{multiplier}x</span>
        <span className="text-[10px] md:text-xs font-semibold mt-0.5">
          {t('membership:extra-pack.card.value-badge-label', {
            defaultValue: 'Value',
          })}
        </span>
      </div>
    </div>
  )
}
